import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import {
  Add as AddIcon,
  CropSquare as RectangleIcon,
  Circle as CircleIcon,
  ChangeHistory as DiamondIcon,
  Panorama as EllipseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import ResizableNoteNode from "./ResizableNoteNode";
import NodeToolbar from "./NodeToolbar";
import DeleteableEdge from "./DeleteableEdge";
import ConnectionPreview from "./ConnectionPreview";
import LiveCursor from "./LiveCursor";
import DraggingNodePreview from "./DraggingNodePreview";
import { useSocket } from "../hooks/useSocket";
import { NoteNodeData, ResizableNoteNodeRef } from "../types";
import RetrospectiveHeader from "./RetrospectiveHeader";

// Import the common hooks
import { useRetrospectiveData } from "../hooks/useRetrospectiveData";
import { useRetrospectiveHandlers } from "../hooks/useRetrospectiveHandlers";
import { useRetrospectiveSocketListeners } from "../hooks/useRetrospectiveSocket";
import { useRetrospectiveRendering } from "../hooks/useRetrospectiveRendering";

interface FlowDiagramProps {
  sprintId: string;
  sprintName: string;
  reactFlow: any;
  onBack: () => void;
  templateId: string;
  parentData?: any;
}

const CustomConnectionLine: any = ({ fromX, fromY, toX, toY }: any) => {
  return (
    <g>
      <path
        fill="none"
        stroke="#1976d2"
        strokeWidth={3}
        strokeDasharray="5,5"
        d={`M${fromX},${fromY} C ${fromX} ${toY} ${fromX} ${toY} ${toX},${toY}`}
      />
      <circle
        cx={toX}
        cy={toY}
        fill="#1976d2"
        r={3}
        stroke="#fff"
        strokeWidth={1.5}
      />
    </g>
  );
};

const FlowDiagramInner: React.FC<FlowDiagramProps> = ({
  sprintId,
  sprintName,
  reactFlow,
  onBack,
  parentData,
}) => {
  const {
    ReactFlow,
    addEdge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    useReactFlow,
  } = reactFlow;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [connectionNodeId, setConnectionNodeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const reactFlowInstance = useReactFlow();
  const flowRef = useRef<HTMLDivElement>(null);
  const lastViewportRef = useRef<any>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nodeRefs = useRef<Map<string, ResizableNoteNodeRef>>(new Map());

  // Static values for templateId and templateName
  const templateId = "flow-diagram-v1";
  const templateName = "Flow Diagram";

  // Initialize socket connection
  const {
    socket,
    connected,
    cursors,
    users,
    draggingNodes,
    activeConnections,
    editingNodes,
    emit,
    on,
    updateCursorPosition,
    updateNodeDragPosition,
    updateTextChange,
    endNodeDrag,
    sendConnectionPreview,
    endConnectionPreview,
    startNodeEditing,
    endNodeEditing,
    userId,
    userName,
    userColor,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
  } = useSocket(sprintId, sprintName, parentData);

  // Function to prepare template data for API
  const prepareTemplateData = useCallback(() => {
    const canvasWidth = 1200;
    const canvasHeight = 800;

    const flowNodes = nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: {
        ...node.data,
        reactions: node.data.reactions || [],
      },
      style: node.style || {},
      timestamp: Date.now(),
    }));

    const flowEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type,
      style: edge.style || {},
      markerEnd: edge.markerEnd,
      timestamp: Date.now(),
    }));

    return [
      {
        flowData: {
          nodes: flowNodes,
          edges: flowEdges,
        },
        canvasMetadata: {
          width: canvasWidth,
          height: canvasHeight,
          // viewport: reactFlowInstance.getViewport(),
        },
        statistics: {
          totalNodes: nodes.length,
          totalEdges: edges.length,
          totalReactions: nodes.reduce(
            (total, node) => total + (node.data.reactions?.length || 0),
            0
          ),
          userCount: new Set([
            ...nodes.map((node) => node.data.userId || userId).filter(Boolean),
            ...nodes
              .flatMap((node) => node.data.reactions || [])
              .map((reaction) => reaction.userId)
              .filter(Boolean),
          ]).size,
        },
        lastUpdated: new Date().toISOString(),
      },
    ];
  }, [nodes, edges, reactFlowInstance, userId]);

  // Use common hooks
  const {
    isDataLoaded,
    setIsDataLoaded,
    lastSaveTime,
    saveStatus,
    hasPendingSave,
    markUserChange,
    saveFlowData,
    loadExistingData,
    isLoadingSprintDetails,
    isUpdatingSprint,
  } = useRetrospectiveData(
    sprintId,
    templateId,
    templateName,
    nodes,
    edges,
    reactFlowInstance,
    userId,
    prepareTemplateData
  );

  const {
    handleNodeDelete,
    handleEdgeDelete,
    handleNodeColorChange,
    handleNodeTextChange,
    handleNodeTextChangeRealtime,
    handleNodeEditStart,
    handleNodeEditEnd,
    handleNodeEmojiReactionAdd,
    handleNodeEmojiReactionToggle,
    handleAddComment,
    handleAddCommentReaction,
    handleRemoveCommentReaction,
    handleNodesChange,
    handleEdgesChange,
  } = useRetrospectiveHandlers(
    nodes,
    setNodes,
    setEdges,
    edges,
    emit,
    userId,
    userName,
    userColor,
    users,
    markUserChange,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
    updateTextChange,
    startNodeEditing,
    endNodeEditing,
    updateNodeDragPosition,
    endNodeDrag,
    onNodesChange,
    onEdgesChange
  );

  const { getCursorsData, getDraggingPreviewsData, getConnectionPreviewsData } =
    useRetrospectiveRendering(
      cursors,
      draggingNodes,
      activeConnections,
      nodes,
      users,
      reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 },
      reactFlowInstance
    );

  // Socket listeners
  useRetrospectiveSocketListeners(
    connected,
    on,
    setNodes,
    setEdges,
    reactFlowInstance,
    userId
  );

  // Save data when user makes changes
  useEffect(() => {
    if (
      isDataLoaded &&
      hasPendingSave &&
      (nodes.length > 0 || edges.length > 0)
    ) {
      saveFlowData();
    }
  }, [nodes, edges, isDataLoaded, hasPendingSave, saveFlowData]);

  // Load existing data when sprint details are available
  useEffect(() => {
    if (!isLoadingSprintDetails && !isDataLoaded) {
      const timer = setTimeout(() => {
        loadExistingData(setNodes, setEdges);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    isLoadingSprintDetails,
    isDataLoaded,
    loadExistingData,
    setNodes,
    setEdges,
  ]);

  // Define custom node types with refs
  const nodeTypes: any = useMemo(
    () => ({
      resizableNote: (props: any) => {
        const editingState = editingNodes.get(props.id);
        const isBeingEditedByOther =
          editingState && editingState.userId !== userId;

        return (
          <ResizableNoteNode
            {...props}
            ref={(nodeRef: any) => {
              if (nodeRef) {
                nodeRefs.current.set(props.id, nodeRef);
              } else {
                nodeRefs.current.delete(props.id);
              }
            }}
            onCursorMove={handleNodeCursorMove}
            onTextChange={handleNodeTextChange}
            onTextChangeRealtime={handleNodeTextChangeRealtime}
            onEditStart={handleNodeEditStart}
            onEditEnd={handleNodeEditEnd}
            onEmojiToggle={handleNodeEmojiReactionToggle}
            isBeingEditedByOther={isBeingEditedByOther}
            editingUser={editingState}
            currentUserId={userId}
            isSelected={selectedNodeId === props.id}
            reactFlow={reactFlow}
            onAddComment={handleAddComment}
            onAddCommentReaction={handleAddCommentReaction}
            onRemoveCommentReaction={handleRemoveCommentReaction}
            currentUserName={userName}
            currentUserColor={userColor}
          />
        );
      },
    }),
    [editingNodes, userId, selectedNodeId, userName, userColor, reactFlow]
  );

  // Define custom edge types
  const edgeTypes: any = useMemo(
    () => ({
      deleteable: (props: any) => (
        <DeleteableEdge
          {...props}
          onDelete={handleEdgeDelete}
          reactFlow={reactFlow}
        />
      ),
    }),
    [handleEdgeDelete, reactFlow]
  );

  // Handle node selection
  const handleNodeClick = useCallback((event: React.MouseEvent, node: any) => {
    event.stopPropagation();
    setSelectedNodeId(node.id);
  }, []);

  // Handle pane click to clear selection and save any editing nodes
  const handlePaneClick = useCallback(() => {
    nodeRefs.current.forEach((nodeRef, nodeId) => {
      if (nodeRef.isEditing) {
        nodeRef.handleSave();
      }
    });
    setSelectedNodeId(null);
  }, []);

  // Handle cursor movement over nodes
  const handleNodeCursorMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();

        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;

        updateCursorPosition({ x, y });
      }
    },
    [reactFlowInstance, updateCursorPosition]
  );

  const handleMouseUp = useCallback(() => {
    if (isConnecting) {
      setIsConnecting(false);
      setConnectionStart(null);
      setConnectionNodeId(null);
      endConnectionPreview();
    }
  }, [isConnecting, endConnectionPreview]);

  const handleMouseLeave = useCallback(() => {
    if (isConnecting) {
      setIsConnecting(false);
      setConnectionStart(null);
      setConnectionNodeId(null);
      endConnectionPreview();
    }
  }, [isConnecting, endConnectionPreview]);

  // Handle mouse movement in the flow
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();

        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;

        updateCursorPosition({ x, y });

        if (isConnecting && connectionStart) {
          sendConnectionPreview(connectionStart, { x, y });
        }
      }
    },
    [
      reactFlowInstance,
      updateCursorPosition,
      isConnecting,
      connectionStart,
      sendConnectionPreview,
    ]
  );

  // Handle connection start
  const handleConnectStart = useCallback(
    (event: any, { nodeId, handleId, handleType }: any) => {
      setIsConnecting(true);
      setConnectionNodeId(nodeId);

      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }

      const node = nodes.find((n) => n.id === nodeId);
      if (node && flowRef.current) {
        const nodeWidth = node.style?.width || node.data?.width || 120;
        const nodeHeight = node.style?.height || node.data?.height || 80;

        let handleX = node.position.x;
        let handleY = node.position.y;
        if (handleId === "source") {
          handleX += nodeWidth;
          handleY += nodeHeight / 2;
        } else if (handleId === "target") {
          handleY += nodeHeight / 2;
        }

        setConnectionStart({ x: handleX, y: handleY });
      }
    },
    [nodes]
  );

  // Handle connection end with proper cleanup
  const handleConnectEnd = useCallback(
    (event: any) => {
      setIsConnecting(false);
      setConnectionStart(null);
      setConnectionNodeId(null);
      endConnectionPreview();

      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    },
    [endConnectionPreview]
  );

  // Handle edge connections
  const onConnect: any = useCallback(
    (params: any) => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      const newEdge = {
        id: `edge-${Date.now()}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: "deleteable",
        style: { stroke: "#1976d2", strokeWidth: 2 },
        markerEnd: {
          type: "arrowclosed",
          color: "#1976d2",
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
      markUserChange();

      emit("EDGE_ADDED", {
        edge: newEdge,
        addedBy: userId,
      });

      setIsConnecting(false);
      setConnectionStart(null);
      setConnectionNodeId(null);
      endConnectionPreview();
    },
    [setEdges, emit, userId, endConnectionPreview, markUserChange, addEdge]
  );

  // Handle viewport changes
  const handleViewportChange = useCallback(
    (viewport: any) => {
      if (lastViewportRef.current) {
        clearTimeout(lastViewportRef.current);
      }

      lastViewportRef.current = setTimeout(() => {
        emit("VIEWPORT_CHANGED", {
          viewport,
          changedBy: userId,
        });
      }, 100);
    },
    [emit, userId]
  );

  // Add node with specific shape
  const addNode = useCallback(
    (shape: "rectangle" | "circle" | "diamond" | "ellipse") => {
      const position = {
        x: Math.random() * 400 + 100,
        y: Math.random() * 300 + 100,
      };

      const newNode = {
        id: `node-${Date.now()}-${userId}`,
        type: "resizableNote",
        position,
        data: {
          text: "",
          width: 120,
          height: 120,
          color: "#fff59d",
          shape: shape,
          userId: userId,
          reactions: [],
        } as NoteNodeData,
        style: {
          width: 120,
          height: 120,
        },
      };

      setNodes((nds) => [...nds, newNode]);
      markUserChange();
      emit("NODE_ADDED", {
        node: newNode,
        addedBy: userId,
      });
    },
    [setNodes, emit, userId, markUserChange]
  );

  // Manual save function
  const handleManualSave = useCallback(async () => {
    await saveFlowData();
  }, [saveFlowData]);

  // Cleanup connection timeout on unmount
  useEffect(() => {
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, []);

  const actions = [
    {
      icon: <RectangleIcon />,
      name: "Rectangle",
      onClick: () => addNode("rectangle"),
    },
    {
      icon: <CircleIcon />,
      name: "Circle",
      onClick: () => addNode("circle"),
    },
    {
      icon: <DiamondIcon />,
      name: "Diamond",
      onClick: () => addNode("diamond"),
    },
    {
      icon: <EllipseIcon />,
      name: "Ellipse",
      onClick: () => addNode("ellipse"),
    },
    {
      icon: <SaveIcon />,
      name: "Save Now",
      onClick: handleManualSave,
    },
  ];

  // Add this reset function after the prepareTemplateData function
const resetRetrospective = useCallback(() => {
  // Reset nodes and edges to empty state (Flow diagrams typically start empty)
  setNodes([]);
  setEdges([]);
  
  // Reset viewport to default position and zoom
  if (reactFlowInstance) {
    reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });
    setTimeout(() => {
      reactFlowInstance.fitView({
        padding: 0.2,
        duration: 800,
      });
    }, 100);
  }
  
  // Clear any selected nodes
  setSelectedNodeId(null);
  
  // Mark as user change to trigger save
  markUserChange();
  
  // Emit reset event to other users
  emit("RETROSPECTIVE_RESET", {
    resetBy: userId,
    timestamp: Date.now(),
  });
}, [setNodes, setEdges, reactFlowInstance, markUserChange, emit, userId]);


  // Render functions
  const renderCursors = () => {
    const cursorsData = getCursorsData();
    return cursorsData.map((cursor) => (
      <LiveCursor
        key={cursor.key}
        x={cursor.x}
        y={cursor.y}
        userName={cursor.userName}
        color={cursor.color}
      />
    ));
  };

  const renderDraggingPreviews = () => {
    const draggingData = getDraggingPreviewsData();
    return draggingData.map((dragging) => (
      <DraggingNodePreview
        key={dragging.key}
        x={dragging.x}
        y={dragging.y}
        width={dragging.width}
        height={dragging.height}
        color={dragging.color}
        userName={dragging.userName}
        shape={dragging.shape}
      />
    ));
  };

  const renderConnectionPreviews = () => {
    const connectionsData = getConnectionPreviewsData();
    return connectionsData.map((connection) => (
      <ConnectionPreview
        key={connection.key}
        fromX={connection.fromX}
        fromY={connection.fromY}
        toX={connection.toX}
        toY={connection.toY}
        color={connection.color}
      />
    ));
  };

  return (
    <>
      <RetrospectiveHeader
        sprintName={sprintName}
        sprintId={sprintId}
        templateName={templateId}
        connected={connected}
        users={users}
        saveStatus={saveStatus}
        lastSaveTime={lastSaveTime}
        onBack={onBack}
        onReset={resetRetrospective}
      />
      <Box sx={{ height: "85vh", width: "100%", position: "relative" }}>
        <div ref={flowRef} style={{ width: "100%", height: "100%" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onConnectStart={handleConnectStart}
            onConnectEnd={handleConnectEnd}
            onMove={handleViewportChange}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionLineComponent={CustomConnectionLine}
            fitView
            style={{ backgroundColor: "#f8f9fa" }}
            connectionLineStyle={{
              stroke: "#1976d2",
              strokeWidth: 3,
              strokeDasharray: "5,5",
            }}
            defaultEdgeOptions={{
              type: "deleteable",
              style: { stroke: "#1976d2", strokeWidth: 2 },
              markerEnd: {
                type: "arrowclosed",
                color: "#1976d2",
              },
            }}
            multiSelectionKeyCode="Meta"
            selectionKeyCode="Shift"
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#ccc" gap={20} size={1} variant="dots" />
            <Controls
              showZoom={true}
              showFitView={true}
              showInteractive={true}
            />
            <MiniMap
              nodeColor={(node) => node.data?.color || "#fff59d"}
              nodeStrokeWidth={2}
              maskColor="rgba(240, 240, 240, 0.6)"
            />
          </ReactFlow>

          {renderCursors()}
          {renderDraggingPreviews()}
          {renderConnectionPreviews()}

          {selectedNodeId && (
            <NodeToolbar
              node={nodes.find((n) => n.id === selectedNodeId)!}
              onColorChange={handleNodeColorChange}
              onEmojiAdd={handleNodeEmojiReactionAdd}
              onEmojiToggle={handleNodeEmojiReactionToggle}
              onDelete={handleNodeDelete}
              onClose={() => setSelectedNodeId(null)}
              viewport={reactFlowInstance.getViewport()}
              currentUserId={userId}
              onAddComment={handleAddComment}
              onAddCommentReaction={handleAddCommentReaction}
              onRemoveCommentReaction={handleRemoveCommentReaction}
              currentUserName={userName}
              currentUserColor={userColor}
            />
          )}
        </div>

        <SpeedDial
          ariaLabel="Add Node"
          sx={{ position: "absolute", bottom: 16, right: 16 }}
          icon={<SpeedDialIcon openIcon={<AddIcon />} />}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>
      </Box>
    </>
  );
};

const FlowDiagram: React.FC<FlowDiagramProps> = (props) => {
  const { ReactFlowProvider } = props.reactFlow;
  return (
    <ReactFlowProvider>
      <FlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
};

export default FlowDiagram;
